'use client';

import { useIntersectionObserver } from 'hooks';
import type { VideoProps } from '../types';
import { VIDEO_INTERSECTION_OBSERVER_ROOT_MARGIN } from '../constants';
import { IMAGE_LOADING } from '../../image';
import '../styles.css';

/**
 * YouTubeVideo component with lazy loading and privacy-enhanced embedding.
 *
 * Features:
 * - Lazy loading: YouTube iframe loads when near viewport (200px margin, 10% threshold)
 * - Privacy-enhanced: Uses youtube-nocookie.com domain for GDPR compliance
 * - Performance optimized: Defers iframe loading until visible
 * - Thumbnail fallback: Displays video thumbnail before iframe loads
 * - No autoplay: YouTube videos never autoplay (configured in VideoTransformer)
 *
 * @param {VideoProps} props - Component props
 * @param {VideoNode} props.video - Video configuration with src (YouTube embed URL), thumbnailUrl, and youtubeId
 * @param {boolean} [props.loadInstantly=false] - If true, renders iframe immediately (for above-the-fold videos)
 *
 * @example
 * // Above-the-fold YouTube video
 * <YouTubeVideo video={{ src: 'https://www.youtube-nocookie.com/embed/...', thumbnailUrl: '...' }} loadInstantly={true} />
 *
 * @example
 * // Below-the-fold YouTube video with lazy loading
 * <YouTubeVideo video={{ src: 'https://www.youtube-nocookie.com/embed/...', thumbnailUrl: '...' }} loadInstantly={false} />
 *
 * Behavior Matrix:
 * ┌────────────────┬────────────────┬─────────────────────────────────────────────────────────┐
 * │ loadInstantly  │ isIntersecting │ Behavior                                                │
 * ├────────────────┼────────────────┼─────────────────────────────────────────────────────────┤
 * │ true           │ -              │ Loads iframe immediately (above-the-fold)               │
 * │ false          │ true           │ Loads iframe when near viewport (lazy loaded)           │
 * │ false          │ false          │ Shows thumbnail, defers iframe until near viewport      │
 * └────────────────┴────────────────┴─────────────────────────────────────────────────────────┘
 *
 * Technical notes:
 * - YouTube autoplay is disabled for performance (configured in VideoTransformer)
 * - Uses youtube-nocookie.com for privacy compliance
 * - Thumbnail displays while iframe loads or when not yet visible
 * - Device-specific thumbnails: sddefault (mobile 640x480), maxresdefault (desktop 1280x720)
 * - Intersection Observer has 200px rootMargin and 0.1 threshold
 * - Minimal iframe permissions: encrypted-media, picture-in-picture, clipboard-write
 */
export function YouTubeVideo(props: VideoProps) {
  const {
    video: { src, thumbnailUrl, caption },
    loadInstantly = false,
  } = props;

  const { ref: containerRef, isIntersecting } = useIntersectionObserver<HTMLDivElement>({
    rootMargin: VIDEO_INTERSECTION_OBSERVER_ROOT_MARGIN,
    threshold: 0.1,
  });

  const shouldLoadVideo = loadInstantly || isIntersecting;

  const videoCaption = caption || 'Video thumbnail';

  return (
    <div className="video-container" ref={containerRef}>
      {shouldLoadVideo ? (
        <iframe
          src={src}
          title={videoCaption}
          allow="encrypted-media; picture-in-picture; clipboard-write;"
          allowFullScreen
          loading={IMAGE_LOADING.LAZY}
        />
      ) : (
        <img src={thumbnailUrl} alt={videoCaption} width="100%" height="auto" loading={IMAGE_LOADING.LAZY} />
      )}
    </div>
  );
}
